import React, { useState, useEffect } from 'react';
import { Check, X, Clock, FileText, User, Car, Inbox } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/api';
import { toast } from 'sonner';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { useTranslation } from 'react-i18next';

const TYPE_LABELS = {
  delete_work_order: { label: 'Supprimer fiche de travail', icon: FileText, color: 'text-red-600 bg-red-50' },
  deactivate_client: { label: 'Desactiver client', icon: User, color: 'text-orange-600 bg-orange-50' },
  deactivate_vehicle: { label: 'Desactiver vehicule', icon: Car, color: 'text-orange-600 bg-orange-50' },
};

const DeletionRequestsPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadRequests(); }, [filter]);

  const loadRequests = async () => {
    try {
      const res = await axios.get(`${API}/deletion-requests`, { params: { status: filter } });
      setRequests(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleAction = async (id, action) => {
    try {
      await axios.patch(`${API}/deletion-requests/${id}/${action}`);
      toast.success(action === 'approve' ? 'Demande approuvee et executee' : 'Demande refusee');
      loadRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-8 space-y-6">
        <div>
          <h1 className="text-4xl font-heading font-bold text-slate-900" data-testid="deletion-requests-title">
            Demandes de suppression
          </h1>
          <p className="text-base text-slate-600 mt-1" data-testid="deletion-requests-count">
            {filter === 'pending' ? `${requests.length} demande(s) en attente` : `${requests.length} demande(s)`}
          </p>
        </div>

        <div className="flex gap-2">
          {[
            { key: 'pending', label: 'En attente' },
            { key: 'approved', label: 'Approuvees' },
            { key: 'rejected', label: 'Refusees' },
            { key: 'all', label: 'Toutes' },
          ].map(tab => (
            <Button
              key={tab.key}
              variant={filter === tab.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(tab.key)}
              data-testid={`filter-${tab.key}`}
            >
              {tab.label}
            </Button>
          ))}
        </div>

        {loading ? (
          <p className="text-center text-slate-500 py-8">{t('common.loading')}</p>
        ) : requests.length === 0 ? (
          <Card className="p-12 text-center rounded-xl" data-testid="no-requests">
            <Inbox className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">Aucune demande</p>
          </Card>
        ) : (
          <div className="grid gap-3">
            {requests.map((req) => {
              const typeInfo = TYPE_LABELS[req.type] || { label: req.type, icon: FileText, color: 'text-slate-600 bg-slate-50' };
              const TypeIcon = typeInfo.icon;
              return (
                <Card
                  key={req.id}
                  className={`p-4 rounded-xl border shadow-sm ${req.status === 'pending' ? 'border-amber-200 bg-amber-50/30' : req.status === 'approved' ? 'border-emerald-200 bg-emerald-50/20' : 'border-slate-200 opacity-60'}`}
                  data-testid={`deletion-request-${req.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${typeInfo.color}`}>
                        <TypeIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-900">{typeInfo.label}</p>
                        <p className="text-xs text-slate-600 mt-0.5">{req.target_label}</p>
                        <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                          <span>Par: <strong>{req.requested_by_name}</strong></span>
                          <span>{new Date(req.created_at).toLocaleString('fr-CH')}</span>
                          {req.status !== 'pending' && (
                            <span className={`font-medium ${req.status === 'approved' ? 'text-emerald-600' : 'text-red-500'}`}>
                              {req.status === 'approved' ? 'Approuvee' : 'Refusee'} par {req.resolved_by_name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {req.status === 'pending' && (
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => handleAction(req.id, 'approve')}
                          data-testid={`approve-${req.id}`}
                        >
                          <Check className="w-4 h-4 mr-1" /> Approuver
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => handleAction(req.id, 'reject')}
                          data-testid={`reject-${req.id}`}
                        >
                          <X className="w-4 h-4 mr-1" /> Refuser
                        </Button>
                      </div>
                    )}
                    {req.status !== 'pending' && (
                      <div className="flex items-center gap-1">
                        {req.status === 'approved' ? (
                          <span className="text-xs font-medium text-emerald-600 bg-emerald-100 px-2 py-1 rounded">Approuvee</span>
                        ) : (
                          <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-1 rounded">Refusee</span>
                        )}
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default DeletionRequestsPage;
